import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useProgramRegistrationQuery } from '../../../hooks/usePrograms';
import ApproveModal from '../../registrations/ApproveModal';
import RejectModal from '../../registrations/RejectModal';
import { ProgramDetails } from './ProgramDetails';

const ProgramRegistrationsModal = ({
  open,
  onClose,
  registration,
  programId,
  institute
}) => {
  const [isApproveModalOpened, setIsApproveModalOpened] = React.useState(false);
  const [isRejectModalOpened, setIsRejectModalOpened] = React.useState(false);

  const queryClient = useQueryClient();
  const { data: programData } =
    useProgramRegistrationQuery(programId);
  
  const overdueDays = React.useMemo(() => {
    if (!registration) return 0;
    const createdAt = new Date(registration.created_at);
    const now = new Date();
    const diffTime = Math.abs(now - createdAt);
    const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  }, [registration]);

  const openApproveModal = React.useCallback(() => {
    setIsApproveModalOpened(programId !== undefined);
  }, [programId]);

  const openRejectModal = React.useCallback(() => {
    setIsRejectModalOpened(programId !== undefined);
  }, [programId]);

  const closeApproveModal = React.useCallback(() => {
    setIsApproveModalOpened(false);
  }, []);

  const closeRejectModal = React.useCallback(() => {
    setIsRejectModalOpened(false);
  }, []);

  const handleRefresh = React.useCallback(() => {
    queryClient.invalidateQueries(['programRegistration', programId]);
    queryClient.invalidateQueries(['registrations']);
    onClose();
  }, [queryClient, programId, onClose]);

  const isWarning = React.useMemo(
    () =>
      overdueDays > institute?.registration_warning_days &&
      overdueDays <= institute?.registration_overdue_days,
    [overdueDays, institute]
  );
  const showOverdue = React.useMemo(
    () => overdueDays > institute?.registration_warning_days,
    [overdueDays, institute]
  );


  if (!programData) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: '#1565c0'
          }}
        >
          Action Program Registration
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {showOverdue && (
            <Box sx={{ mb: 2 }}>
              <Chip
                label={`Overdue ${overdueDays} days`}
                size="small"
                style={{
                  backgroundColor: isWarning ? '#FFB300' : '#E53935',
                  color: 'white'
                }}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="body2" component="span">
                <strong>Requested:</strong>{' '}
              </Typography>
              <Typography variant="body2" component="span">
                {new Date(registration.created_at).toLocaleDateString('en-AU')}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" component="span">
                <strong>Educator:</strong>{' '}
              </Typography>
              <Typography variant="body2" component="span">
                {registration?.user_name}
              </Typography>
            </Box>

            {institute?.custom_user_field_1_enabled && institute?.custom_user_field_1_on_registrations ? (
              <Box>
                <Typography variant="body2" component="span">
                  <strong>{institute?.custom_user_field_1}:</strong>{' '}
                </Typography>
                <Typography variant="body2" component="span">
                  {registration?.custom_1 || <em>Not Set</em>}
                </Typography>
              </Box>
            ) : null}

            {institute?.custom_user_field_2_enabled && institute?.custom_user_field_2_on_registrations ? (
              <Box>
                <Typography variant="body2" component="span">
                  <strong>{institute?.custom_user_field_2}:</strong>{' '}
                </Typography>
                <Typography variant="body2" component="span">
                  {registration?.custom_2 || <em>Not Set</em>}
                </Typography>
              </Box>
            ) : null}
          </Box>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Program details
          </Typography>
          <ProgramDetails program={programData} />

          <Box
            sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}
          >
            <Button
              variant="contained"
              color="success"
              onClick={openApproveModal}
            >
              APPROVE
            </Button>
            <Button variant="contained" color="error" onClick={openRejectModal}>
              INCOMPLETE
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <ApproveModal
        isOpened={isApproveModalOpened}
        onClose={closeApproveModal} 
        registration={registration}
        onRefreshRequest={handleRefresh}
      />
      <RejectModal
        isOpened={isRejectModalOpened}
        onClose={closeRejectModal}
        registration={registration}
        onRefreshRequest={handleRefresh}
      />
    </>
  );
};

export default ProgramRegistrationsModal;
